// Billing · post-checkout polling state + checkout launcher.
// Extracted from BillingTab so the UI stays presentational.
import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";

const POLL_INTERVAL_MS = 2500;
const MAX_POLLS = 24; // 60 seconds total

export function useBillingCheckout({ onPlanChanged } = {}) {
  const [packages, setPackages] = useState([]);
  const [busy, setBusy] = useState("");
  const [polling, setPolling] = useState(false);
  const [search, setSearch] = useSearchParams();

  useEffect(() => {
    api.get("/billing/plans").then((r) => setPackages(r.data));
  }, []);

  const pollStatus = useCallback(async (sessionId, attempts = 0) => {
    if (attempts >= MAX_POLLS) {
      setPolling(false);
      toast.error("Couldn't confirm payment. Check your email or refresh in a minute.");
      return;
    }
    try {
      const { data } = await api.get(`/billing/checkout/status/${sessionId}`);
      if (data.payment_status === "paid") {
        setPolling(false);
        toast.success(`Your ${data.plan} plan is active!`);
        onPlanChanged && onPlanChanged();
        search.delete("paypal"); search.delete("subscription_id"); search.delete("token");
        setSearch(search, { replace: true });
        return;
      }
      if (data.status === "expired" || data.status === "cancelled") {
        setPolling(false);
        toast.error("Payment session expired.");
        return;
      }
      setTimeout(() => pollStatus(sessionId, attempts + 1), POLL_INTERVAL_MS);
    } catch {
      setTimeout(() => pollStatus(sessionId, attempts + 1), POLL_INTERVAL_MS);
    }
  }, [onPlanChanged, search, setSearch]);

  // Detect post-checkout redirect (?paypal=success&subscription_id=…|token=…).
  useEffect(() => {
    const result = search.get("paypal");
    const sid = search.get("subscription_id") || search.get("token");
    if (result === "success" && sid) {
      setPolling(true);
      toast.info("Confirming your PayPal payment…");
      pollStatus(sid, 0);
    } else if (result === "cancelled") {
      toast.message("PayPal checkout cancelled.");
      search.delete("paypal");
      setSearch(search, { replace: true });
    }
  }, [search, setSearch, pollStatus]);

  const startCheckout = async (packageId) => {
    setBusy(packageId);
    try {
      const { data } = await api.post("/billing/checkout", {
        package_id: packageId,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout.");
      setBusy("");
    }
  };

  return { packages, busy, polling, startCheckout };
}
